/**
 * File Download Validator Utility
 * Follows Azure Static Web Apps best practices for client-side file handling
 */

export interface DownloadValidationResult {
  isValid: boolean;
  fileSize: number;
  expectedSize?: number;
  errors: string[];
  warnings: string[];
}

export interface DownloadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Validates XLSX file integrity by checking ZIP structure
 */
export const validateXLSXIntegrity = async (data: ArrayBuffer): Promise<boolean> => {
  try {
    // XLSX files are ZIP archives, check for ZIP signature
    const uint8Array = new Uint8Array(data);
    
    // Check ZIP file signature (PK)
    if (uint8Array.length < 4) return false;
    if (uint8Array[0] !== 0x50 || uint8Array[1] !== 0x4B) return false;
    
    // Check for ZIP central directory signature at the end
    if (uint8Array.length < 22) return false;
    
    // Look for End of Central Directory Record signature
    for (let i = uint8Array.length - 22; i >= 0; i--) {
      if (uint8Array[i] === 0x50 && uint8Array[i + 1] === 0x4B && 
          uint8Array[i + 2] === 0x05 && uint8Array[i + 3] === 0x06) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('[XLSX Validation Error]', error);
    return false;
  }
};

/**
 * Validates downloaded file completeness - Enhanced for ArrayBuffer
 */
export const validateDownloadCompleteness = async (
  data: ArrayBuffer,
  expectedContentLength?: number,
  contentType?: string
): Promise<DownloadValidationResult> => {
  const result: DownloadValidationResult = {
    isValid: true,
    fileSize: data.byteLength,
    expectedSize: expectedContentLength,
    errors: [],
    warnings: []
  };

  // Check if data exists and has size
  if (!data || data.byteLength === 0) {
    result.isValid = false;
    result.errors.push('Downloaded file is empty or invalid');
    return result;
  }

  // Validate expected content length if provided
  if (expectedContentLength && data.byteLength !== expectedContentLength) {
    result.isValid = false;
    result.errors.push(
      `File size mismatch. Expected: ${expectedContentLength} bytes, Got: ${data.byteLength} bytes`
    );
  }

  // XLSX-specific validation - Enhanced for ArrayBuffer
  if (contentType?.includes('spreadsheetml') || contentType?.includes('xlsx')) {
    const isValidXLSX = await validateXLSXIntegrity(data);
    if (!isValidXLSX) {
      result.isValid = false;
      result.errors.push('XLSX file structure is invalid or corrupted');
    }
  }

  // File size reasonableness check (prevent extremely small files)
  if (data.byteLength < 1024) { // Less than 1KB
    result.warnings.push('File size is unusually small, may be incomplete');
  }

  return result;
};

/**
 * Enhanced axios config with progress tracking and validation
 * Fixed for Azure Static Web Apps binary file handling
 */
export const createDownloadConfig = (
  onProgress?: (progress: DownloadProgress) => void,
  timeoutMs: number = 300000, // 5 minutes default
  responseType: 'blob' | 'arraybuffer' = 'arraybuffer' // Fixed: Added third parameter
) => {
  return {
    responseType,
    timeout: timeoutMs,
    onDownloadProgress: (progressEvent: any) => {
      if (onProgress && progressEvent.total) {
        const progress: DownloadProgress = {
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
        };
        onProgress(progress);
      }
    },
    // Ensure we validate response
    validateStatus: (status: number) => status === 200,
  };
};